import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import ExamIntroPage from './pages/ExamIntroPage';
import ExamQuestionPage from './pages/ExamQuestionPage';
import ExamResultsPage from './pages/ExamResultsPage';
import IntroductionAudio from '../lesson/IntroductionAudio';

const ExamViewer = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const courseId = searchParams.get('courseId');
  
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [examResult, setExamResult] = useState(null);

  useEffect(() => {
    if (!courseId) {
      setError('Course ID is required');
      setLoading(false);
      return;
    }

    const loadExam = async () => {
      try {
        const response = await api.get(`/courses/${courseId}/exam`);
        setExam(response.data.data.exam);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load exam');
        setLoading(false);
      }
    };

    loadExam();
  }, [courseId]);

  const handleAnswer = (questionId, answerId) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answerId
    }));
  };

  const handleNext = () => {
    if (!exam || !exam.pages) return;
    
    const currentPage = exam.pages[currentPageIndex];
    

    if (currentPage.type === 'exam-results') {
      return;
    }

    
    if (currentPage.type === 'exam-question' && currentPageIndex === exam.pages.length - 2) {
      handleSubmitExam();
      return;
    }

    if (currentPageIndex < exam.pages.length - 1) {
      setCurrentPageIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex(prev => prev - 1);
    }
  };

  const handleSubmitExam = async () => {
    if (!exam || submitting) return;

    setSubmitting(true);

    try {
      
      const answerArray = Object.entries(answers).map(([questionId, answerId]) => ({
        questionId,
        answer: answerId
      }));

      const response = await api.post(`/courses/${courseId}/exam/submit`, {
        answers: answerArray
      });

      const result = response.data.data;
      const totalQuestions = exam.pages.filter(p => p.type === 'exam-question').length;
      const correctAnswers = result.examAttempt.score 
        ? Math.round((result.examAttempt.score / 100) * totalQuestions)
        : 0;
      
      setExamResult({
        score: result.examAttempt.score,
        passed: result.examAttempt.passed,
        totalQuestions: totalQuestions,
        correctAnswers: correctAnswers,
        message: result.message,
        progressReset: result.progressReset || false,
        certificate: result.certificate || null
      });

      const resultsPageIndex = exam.pages.findIndex(p => p.type === 'exam-results');
      if (resultsPageIndex !== -1) {
        setCurrentPageIndex(resultsPageIndex);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit exam');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E2F2FF] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading exam...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#E2F2FF] flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-md p-8 max-w-md">
          <div className="text-red-600 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold mb-2 text-gray-800">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/dashboard/modules')}
            className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!exam || !exam.pages || exam.pages.length === 0) {
    return (
      <div className="min-h-screen bg-[#E2F2FF] flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-md p-8 max-w-md text-center">
          <p className="text-gray-600 mb-4">No exam data available</p>
          <button
            onClick={() => navigate('/dashboard/modules')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const currentPage = exam.pages[currentPageIndex];
  const isFirstPage = currentPageIndex === 0;
  const isLastPage = currentPageIndex === exam.pages.length - 1;
  const isResultsPage = currentPage.type === 'exam-results';
  const isQuestionPage = currentPage.type === 'exam-question';
  const canProceed = isQuestionPage ? answers[currentPage.id] !== undefined : true;

  const renderPage = () => {
    switch (currentPage.type) {
      case 'exam-intro':
        return <ExamIntroPage page={currentPage} />;
      
      case 'exam-question':
        return (
          <ExamQuestionPage
            page={currentPage}
            selectedAnswer={answers[currentPage.id]}
            onAnswer={(answerId) => handleAnswer(currentPage.id, answerId)}
          />
        );
      
      case 'exam-results':
        return <ExamResultsPage page={currentPage} examResult={examResult} />;
      
      default:
        return <div>Unknown page type: {currentPage.type}</div>;
    }
  };

  return (
    <div className="min-h-screen bg-[#E2F2FF]">
      {/* Header */}
      <header className="bg-primary text-white px-6 py-4 shadow-md">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">{exam.title || 'Final Exam'}</h1>
            {exam.description && (
              <p className="text-sm text-blue-100">{exam.description}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/dashboard')}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-2 rounded-lg transition-colors font-medium"
            >
              Dashboard
            </button>
            <button 
              onClick={() => navigate('/dashboard/settings')}
              className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-colors"
              title="Settings"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-md p-6 lg:p-8">
          {renderPage()}
        </div>
      </main>

      {!isResultsPage && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <button
              onClick={handlePrevious}
              disabled={isFirstPage}
              className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                isFirstPage
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Previous
            </button>

            <div className="text-sm text-gray-600">
              Page {currentPageIndex + 1} of {exam.pages.length}
            </div>

            <button
              onClick={handleNext}
              disabled={!canProceed || submitting || isLastPage}
              className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                !canProceed || submitting || isLastPage
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-primary text-white hover:bg-blue-700'
              }`}
            >
              {submitting ? 'Submitting...' : isQuestionPage && currentPageIndex === exam.pages.length - 2 ? 'Submit Exam' : 'Next'}
            </button>
          </div>
        </div>
      )}

      {isResultsPage && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-center">
            <button
              onClick={() => navigate('/dashboard/modules')}
              className="px-6 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-blue-700"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamViewer;

